import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useFavorites } from "@/hooks/useFavorites";
import FavoritesPanel from "@/components/feature/FavoritesPanel";

const NAV_ITEMS = [
  { to: "/", label: "首页", end: true },
  { to: "/category/erotic", label: "伦理电影" },
  { to: "/category/drama", label: "伦理电视剧" },
  { to: "/about", label: "关于我们" },
];

export default function Navbar() {
  const navigate = useNavigate();
  const { count } = useFavorites();
  const [menuOpen, setMenuOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const q = keyword.trim();
    if (!q) {
      navigate("/search");
    } else {
      navigate(`/search?q=${encodeURIComponent(q)}`);
    }
    setMenuOpen(false);
  };

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
      isActive
        ? "text-accent-600"
        : "text-foreground-700 hover:text-primary-700 hover:bg-background-100"
    }`;

  return (
    <>
      <header className="sticky top-0 z-50 bg-background-50/95 backdrop-blur border-b border-background-200">
        <div className="w-full px-4 md:px-6">
          <div className="h-16 flex items-center justify-between gap-3">
            <Link to="/" className="flex items-center gap-2 shrink-0 group">
              <span className="w-9 h-9 flex items-center justify-center rounded-md bg-primary-500 text-background-50">
                <i className="ri-film-line text-lg" />
              </span>
              <span className="flex flex-col leading-none">
                <span className="font-heading font-bold text-lg text-foreground-950 tracking-wide">
                  韩国伦理
                </span>
                <span className="text-[10px] tracking-[0.2em] text-foreground-500 uppercase">
                  Korean Ethic Cinema
                </span>
              </span>
            </Link>

            <nav className="hidden md:flex items-center gap-1" aria-label="主导航">
              {NAV_ITEMS.map((item) => (
                <NavLink key={item.to} to={item.to} end={item.end} className={navLinkClass}>
                  {item.label}
                </NavLink>
              ))}
            </nav>

            <div className="flex items-center gap-2">
              <form
                onSubmit={submitSearch}
                className="hidden lg:flex items-center bg-background-100 border border-background-200 rounded-md h-9 pl-3 pr-1 focus-within:border-primary-400 transition-colors"
              >
                <i className="ri-search-line text-foreground-400 text-sm" />
                <input
                  ref={searchRef}
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  type="search"
                  name="nav-search"
                  placeholder="搜索作品 / 影评"
                  className="bg-transparent outline-none text-sm px-2 w-44 text-foreground-800 placeholder:text-foreground-400"
                />
                <button
                  type="submit"
                  className="h-7 px-3 rounded-md bg-primary-500 text-background-50 text-xs font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  搜索
                </button>
              </form>

              <button
                type="button"
                onClick={() => setPanelOpen(true)}
                className="relative w-9 h-9 flex items-center justify-center rounded-md border border-background-200 text-foreground-700 hover:text-accent-600 hover:border-accent-300 transition-colors cursor-pointer"
                aria-label="我的收藏"
              >
                <i className="ri-bookmark-line text-lg" />
                {count > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 min-w-4 h-4 px-1 rounded-full bg-accent-500 text-background-50 text-[10px] flex items-center justify-center">
                    {count}
                  </span>
                )}
              </button>

              <button
                type="button"
                onClick={() => setMenuOpen(true)}
                className="md:hidden w-9 h-9 flex items-center justify-center rounded-md border border-background-200 text-foreground-800 cursor-pointer"
                aria-label="打开菜单"
              >
                <i className="ri-menu-line text-xl" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {menuOpen && (
        <div className="fixed inset-0 z-[60] md:hidden">
          <div
            className="absolute inset-0 bg-foreground-950/50"
            onClick={() => setMenuOpen(false)}
          />
          <div className="absolute top-0 right-0 h-full w-[78%] max-w-xs bg-background-50 flex flex-col">
            <div className="h-16 px-4 flex items-center justify-between border-b border-background-200">
              <span className="font-heading font-bold text-foreground-950">韩国伦理</span>
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                className="w-9 h-9 flex items-center justify-center rounded-md border border-background-200 text-foreground-800 cursor-pointer"
                aria-label="关闭菜单"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <div className="p-4 flex flex-col gap-4 flex-1 overflow-y-auto">
              <form
                onSubmit={submitSearch}
                className="flex items-center bg-background-100 border border-background-200 rounded-md h-10 pl-3 pr-1 focus-within:border-primary-400"
              >
                <i className="ri-search-line text-foreground-400" />
                <input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  type="search"
                  placeholder="搜索作品 / 影评"
                  className="bg-transparent outline-none text-sm px-2 flex-1 text-foreground-800 placeholder:text-foreground-400"
                />
                <button
                  type="submit"
                  className="h-8 px-3 rounded-md bg-primary-500 text-background-50 text-xs font-medium whitespace-nowrap cursor-pointer"
                >
                  搜索
                </button>
              </form>
              <nav className="flex flex-col" aria-label="移动端导航">
                {NAV_ITEMS.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    onClick={() => setMenuOpen(false)}
                    className={({ isActive }) =>
                      `px-3 py-3 rounded-md text-sm font-medium border-b border-background-100 ${
                        isActive ? "text-accent-600" : "text-foreground-700"
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                ))}
                <Link
                  to="/search"
                  onClick={() => setMenuOpen(false)}
                  className="px-3 py-3 rounded-md text-sm font-medium text-foreground-700"
                >
                  搜索
                </Link>
              </nav>
            </div>
          </div>
        </div>
      )}

      <FavoritesPanel open={panelOpen} onClose={() => setPanelOpen(false)} />
    </>
  );
}